import React, { useMemo, useState } from "react";
import { Box, FormControlLabel, FormHelperText, FormLabel, Radio, RadioGroup, Typography } from "@mui/material";
import WidgetFrame, { OPTION_SX, WidgetButton } from "./WidgetFrame";
import { FORM_ERROR_KEY, NO_SLOT } from "./kinds";
import { groupSlotsByDay, isPastSlot, slotLabel } from "./model/slots";
import { useWidgetMsg } from "../../../hooks/widget/useWidgetMsg";

const DAY_SX = { fontSize: 11, textTransform: "uppercase", letterSpacing: ".6px", color: "var(--w-text)", mb: 0.5, "&.Mui-focused": { color: "var(--w-text)" } };

/**
 * `slot_picker` (spec-widget-mcp 05 Step 8): slots grouped by calendar day IN `payload.timezone`,
 * each day a titled radio group, each slot a radio labelled `"14:30–15:00"`. One shared radio name, so
 * there is one choice across the days. A slot that has started is shown disabled with a *passed* hint,
 * never hidden. `allowNone` adds a final radio with `noneLabel`. Submits `{ slotId }` or
 * `{ slotId: "none" }`. The timezone is stated once in the header.
 */
export default function SlotPickerWidget({ envelope, idPrefix, submitter, now }) {
	const { msg, lang } = useWidgetMsg();
	const p = envelope.payload || {};
	const [slotId, setSlotId] = useState("");
	const days = useMemo(() => groupSlotsByDay(p.slots, p.timezone, lang), [p.slots, p.timezone, lang]);
	const at = typeof now === "number" ? now : Date.now();
	const zoneLine = msg("widget.times_in", "Times in {timezone}", { timezone: p.timezone });
	const subtitle = p.subtitle && p.subtitle.includes(p.timezone) ? p.subtitle : [p.subtitle, zoneLine].filter(Boolean).join(" · ");
	const name = `${idPrefix}-slot`;
	const error = submitter.rejection?.fields?.slotId || submitter.rejection?.fields?.[FORM_ERROR_KEY] || null;
	const errId = error ? `${idPrefix}-slot-error` : undefined;
	const onChange = (e) => setSlotId(e.target.value);

	const radio = (value, label, disabled = false, hint = null) => (
		<Box key={value} data-testid="widget-slot" sx={{ ...OPTION_SX, opacity: disabled ? 0.6 : 1 }}>
			<FormControlLabel
				value={value}
				disabled={disabled}
				control={<Radio size="small" name={name} />}
				label={hint ? `${label} (${hint})` : label}
				sx={{ m: 0, width: "100%", "& .MuiFormControlLabel-label": { fontSize: 12.5 }, "& .MuiFormControlLabel-label.Mui-disabled": { color: "var(--w-text)" } }}
			/>
		</Box>
	);

	return (
		<WidgetFrame
			kind="slot_picker"
			title={p.title}
			subtitle={subtitle}
			footer={(
				<WidgetButton data-testid="widget-submit" onClick={() => submitter.submit({ slotId })} disabled={!slotId || submitter.status === "sending" || !submitter.canSubmit}>
					{p.submitLabel || msg("widget.submit", "Send")}
				</WidgetButton>
			)}
		>
			{days.map((day) => {
				const labelId = `${idPrefix}-day-${day.key}`;
				const inDay = day.slots.some((s) => s.id === slotId);
				return (
					<Box key={day.key}>
						<FormLabel id={labelId} component="p" sx={DAY_SX}>{day.label}</FormLabel>
						<RadioGroup name={name} value={inDay ? slotId : ""} onChange={onChange} aria-labelledby={labelId} sx={{ gap: 0.75 }} aria-describedby={errId}>
							{day.slots.map((s) => {
								const past = isPastSlot(s, at);
								return radio(s.id, slotLabel(s, p.timezone), past, past ? msg("widget.passed", "passed") : null);
							})}
						</RadioGroup>
					</Box>
				);
			})}
			{p.allowNone === true && (
				<RadioGroup name={name} value={slotId === NO_SLOT ? NO_SLOT : ""} onChange={onChange} aria-label={p.noneLabel || msg("widget.none", "None of these work")}>
					{radio(NO_SLOT, p.noneLabel || msg("widget.none", "None of these work"))}
				</RadioGroup>
			)}
			{error && <FormHelperText id={errId} sx={{ mx: 0, color: "var(--w-error)" }}>{error}</FormHelperText>}
			{days.length === 0 && <Typography sx={{ fontSize: 12 }}>{envelope.fallbackText}</Typography>}
		</WidgetFrame>
	);
}
